import React, { useState, useEffect, useCallback } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, InputGroup, Button, Pagination, Badge } from 'react-bootstrap';
import { searchStoricoProdottiCliente } from '../../services/api';
import { debounce } from 'lodash';

function ClienteProdottiTab({ clienteId }) {
    const [prodotti, setProdotti] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Filtri ricerca
    const [searchText, setSearchText] = useState('');
    const [tipoProdotto, setTipoProdotto] = useState('all');
    const [dataDa, setDataDa] = useState('');
    const [dataA, setDataA] = useState('');
    
    // Paginazione
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [limit] = useState(20);
    
    // Debounce per la ricerca
    const debouncedSearch = useCallback(
        debounce((searchValue, tipo, da, a, page) => {
            fetchProdotti(searchValue, tipo, da, a, page);
        }, 500),
        [clienteId]
    );

    const fetchProdotti = async (search = '', tipo = 'all', da = '', a = '', page = 1) => {
        setLoading(true);
        setError(null);
        
        try {
            const params = {
                search: search || undefined,
                tipo_prodotto: tipo === 'all' ? undefined : tipo,
                data_da: da || undefined,
                data_a: a || undefined,
                skip: (page - 1) * limit,
                limit: limit
            };
            
            const response = await searchStoricoProdottiCliente(clienteId, params);
            setProdotti(response.data.risultati);
            setTotal(response.data.total);
        } catch (err) {
            console.error('Errore caricamento prodotti:', err);
            setError('Errore nel caricamento dello storico prodotti.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProdotti(searchText, tipoProdotto, dataDa, dataA, currentPage);
    }, [clienteId]);

    // Gestione ricerca con debounce
    useEffect(() => {
        if (searchText !== '') {
            setCurrentPage(1);
            debouncedSearch(searchText, tipoProdotto, dataDa, dataA, 1);
        } else {
            setCurrentPage(1);
            fetchProdotti('', tipoProdotto, dataDa, dataA, 1);
        }
    }, [searchText]);

    // Gestione cambio filtri
    const handleFiltroChange = () => {
        setCurrentPage(1);
        fetchProdotti(searchText, tipoProdotto, dataDa, dataA, 1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchProdotti(searchText, tipoProdotto, dataDa, dataA, page);
    };

    const resetFiltri = () => {
        setSearchText('');
        setTipoProdotto('all');
        setDataDa('');
        setDataA('');
        setCurrentPage(1);
        fetchProdotti('', 'all', '', '', 1);
    };

    // Calcolo paginazione
    const totalPages = Math.ceil(total / limit);
    const hasResults = prodotti.length > 0;

    return (
        <div>
            {/* Barra di ricerca e filtri */}
            <Row className="mb-3">
                <Col md={6}>
                    <InputGroup>
                        <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Cerca per titolo, SKU, EAN, artista, brand..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        {searchText && (
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => setSearchText('')}
                            >
                                <i className="bi bi-x"></i>
                            </Button>
                        )}
                    </InputGroup>
                </Col>
                
                <Col md={2}>
                    <Form.Select
                        value={tipoProdotto}
                        onChange={(e) => {
                            setTipoProdotto(e.target.value);
                            handleFiltroChange();
                        }}
                    >
                        <option value="all">Tutti i prodotti</option>
                        <option value="film">Solo Film</option>
                        <option value="musica">Solo Musica</option>
                        <option value="collectible">Solo Collectibles</option>
                    </Form.Select>
                </Col>
                
                <Col md={2}>
                    <Form.Control
                        type="date"
                        value={dataDa}
                        onChange={(e) => setDataDa(e.target.value)}
                        onBlur={handleFiltroChange}
                        placeholder="Data da"
                    />
                </Col>
                
                <Col md={2}>
                    <Form.Control
                        type="date"
                        value={dataA}
                        onChange={(e) => setDataA(e.target.value)}
                        onBlur={handleFiltroChange}
                        placeholder="Data a"
                    />
                </Col>
            </Row>

            {/* Reset filtri button */}
            {(searchText || tipoProdotto !== 'all' || dataDa || dataA) && (
                <Row className="mb-2">
                    <Col>
                        <Button variant="link" size="sm" onClick={resetFiltri}>
                            <i className="bi bi-x-circle"></i> Resetta tutti i filtri
                        </Button>
                        <small className="text-muted ms-2">
                            {total} {total === 1 ? 'prodotto trovato' : 'prodotti trovati'}
                        </small>
                    </Col>
                </Row>
            )}

            {/* Messaggio informativo se nessun risultato */}
            {!loading && !hasResults && (
                <Alert variant="info">
                    {searchText || dataDa || dataA ? 
                        'Nessun prodotto trovato con i criteri di ricerca specificati.' :
                        'Questo cliente non ha ancora effettuato acquisti.'
                    }
                </Alert>
            )}

            {/* Loading spinner */}
            {loading && (
                <div className="text-center my-4">
                    <Spinner animation="border" />
                    <p className="mt-2">Caricamento...</p>
                </div>
            )}

            {/* Errore */}
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Tabella risultati */}
            {!loading && hasResults && (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th style={{width: '80px'}}>Tipo</th>
                                <th>SKU</th>
                                <th>Titolo/Nome</th>
                                <th>Condizione</th>
                                <th className="text-center">Ordini</th>
                                <th className="text-center">Qtà Totale</th>
                                <th className="text-end">Prezzo Medio</th>
                                <th>Ultimo Acquisto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {prodotti.map((prodotto, index) => (
                                <tr key={`${prodotto.tipo_prodotto}-${prodotto.prodotto_id}-${index}`}>
                                    <td>
                                        <Badge 
                                            bg={
                                                prodotto.tipo_prodotto === 'Film' ? 'primary' : 
                                                prodotto.tipo_prodotto === 'Musica' ? 'success' : 
                                                'warning'
                                            }
                                        >
                                            {prodotto.tipo_prodotto}
                                        </Badge>
                                    </td>
                                    <td><small className="text-muted">{prodotto.sku}</small></td>
                                    <td>
                                        <strong>{prodotto.titolo}</strong>
                                        {prodotto.artista && <div><small className="text-muted">{prodotto.artista}</small></div>}
                                        {prodotto.brand && <div><small className="text-muted">{prodotto.brand}</small></div>}
                                        {prodotto.anno && <div><small className="text-muted">({prodotto.anno})</small></div>}
                                    </td>
                                    <td>{prodotto.condizione || 'N/A'}</td>
                                    <td className="text-center">
                                        <Badge bg="secondary">{prodotto.numero_ordini}</Badge>
                                    </td>
                                    <td className="text-center">{prodotto.quantita_totale_acquistata}</td>
                                    <td className="text-end">
                                        {prodotto.prezzo_medio_acquisto ? 
                                            `€ ${prodotto.prezzo_medio_acquisto.toFixed(2)}` : 
                                            'N/A'
                                        }
                                    </td>
                                    <td>
                                        {prodotto.data_ultimo_acquisto ? 
                                            new Date(prodotto.data_ultimo_acquisto).toLocaleDateString('it-IT') : 
                                            'N/A'
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {/* Paginazione */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.First 
                                    onClick={() => handlePageChange(1)} 
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev 
                                    onClick={() => handlePageChange(currentPage - 1)} 
                                    disabled={currentPage === 1}
                                />
                                
                                {[...Array(totalPages)].map((_, idx) => {
                                    const page = idx + 1;
                                    if (
                                        page === 1 || 
                                        page === totalPages || 
                                        (page >= currentPage - 2 && page <= currentPage + 2)
                                    ) {
                                        return (
                                            <Pagination.Item
                                                key={page}
                                                active={page === currentPage}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </Pagination.Item>
                                        );
                                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                                        return <Pagination.Ellipsis key={page} disabled />;
                                    }
                                    return null;
                                })}
                                
                                <Pagination.Next 
                                    onClick={() => handlePageChange(currentPage + 1)} 
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last 
                                    onClick={() => handlePageChange(totalPages)} 
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    {/* Info paginazione */}
                    <div className="text-center text-muted mt-2">
                        <small>
                            Mostrando {((currentPage - 1) * limit) + 1}-{Math.min(currentPage * limit, total)} di {total} prodotti
                        </small>
                    </div>
                </>
            )}
        </div>
    );
}

export default ClienteProdottiTab;
